import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/app/auth/context/AuthContext';
import { useDashboardPeriodOptions } from '~/app/hooks/dashboard/useDashboardPeriodOptions';
import { ChargingKpiService } from '~/app/services/charging-kpi/charging-kpi.service';
import { mapChargingKpiView } from '~/app/services/charging-kpi/map-charging-kpi-cards';
import { defaultDashboardPeriod } from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.period';
import { OperatorServiceQualityService } from '~/app/services/operator-service-quality/operator-service-quality.service';
import type { DashboardPeriod } from '~/app/services/operator-service-quality/operator-service-quality.types';
import type { ServiceQualityPageViewProps } from '../ServiceQualityPage.types';
import {
  mapCustomerRatingCard,
  mapEscalationNote,
  mapLocationRows,
  mapManagedSegment,
  mapOwnedSegment,
} from '../utils';
import { useLocationsColumns } from './useLocationsColumns';

export function useServiceQualityPage(): ServiceQualityPageViewProps {
  const { t, i18n } = useTranslation('dashboard');
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const periodOptions = useDashboardPeriodOptions();
  const columns = useLocationsColumns();
  const [period, setPeriod] = useState<DashboardPeriod>(() =>
    defaultDashboardPeriod(),
  );

  const statsQuery = useQuery({
    queryKey: ['operator-service-quality', 'stats', organizationId, period],
    queryFn: () =>
      OperatorServiceQualityService.getStats(organizationId, period),
    enabled: !!organizationId,
  });

  const chargingKpisQuery = useQuery({
    queryKey: ['charging-kpis', organizationId, period],
    queryFn: () => ChargingKpiService.getKpis(period),
    enabled: !!organizationId,
  });

  const stats = statsQuery.data;
  const hasOrganization = !!organizationId;
  const chargingKpis = useMemo(
    () =>
      chargingKpisQuery.data
        ? mapChargingKpiView(chargingKpisQuery.data, t)
        : null,
    [chargingKpisQuery.data, t],
  );

  const customerRating = useMemo(
    () => (stats ? mapCustomerRatingCard(stats, i18n.language, t) : null),
    [stats, i18n.language, t],
  );
  const ownedSegment = useMemo(
    () => (stats ? mapOwnedSegment(stats, t) : null),
    [stats, t],
  );
  const managedSegment = useMemo(
    () => (stats ? mapManagedSegment(stats, t) : null),
    [stats, t],
  );
  const locationsRows = useMemo(
    () => (stats ? mapLocationRows(stats, t) : []),
    [stats, t],
  );
  const escalationNote = useMemo(
    () => (stats ? mapEscalationNote(stats, t) : ''),
    [stats, t],
  );

  const onPeriodChange = useCallback((next: DashboardPeriod) => {
    setPeriod(next);
  }, []);

  const onGenerateReport = useCallback(() => {
    // Report export lands in a follow-up ticket.
  }, []);

  return {
    isLoading: hasOrganization && statsQuery.isLoading,
    isError: !hasOrganization || statsQuery.isError,
    errorTitle: t('errors.loadTitle'),
    errorMessage: !hasOrganization
      ? t('errors.missingOrganization')
      : statsQuery.error instanceof Error
        ? statsQuery.error.message
        : t('errors.loadFallback'),
    pageTitle: t('serviceQuality.title'),
    subtitle: t('serviceQuality.subtitle'),
    period,
    periodOptions,
    periodAriaLabel: t('header.period.ariaLabel'),
    generateReportLabel: t('serviceQuality.generateReport'),
    chargingKpisLoading: hasOrganization && chargingKpisQuery.isLoading,
    chargingKpisError: chargingKpisQuery.isError
      ? chargingKpisQuery.error instanceof Error
        ? chargingKpisQuery.error.message
        : t('errors.loadFallback')
      : null,
    sessionSuccess: chargingKpis
      ? {
          title: chargingKpis.sessionSuccess.title,
          metric: chargingKpis.sessionSuccess.metric,
          description: chargingKpis.sessionSuccess.description,
          capacityLeft: chargingKpis.sessionSuccess.capacityLeft,
          capacityRight: chargingKpis.sessionSuccess.capacityRight,
          progressPercent: chargingKpis.sessionSuccess.progressPercent,
        }
      : null,
    weightedUptime: chargingKpis
      ? {
          title: chargingKpis.weightedUptime.title,
          metric: chargingKpis.weightedUptime.metric,
          description: chargingKpis.weightedUptime.description,
          capacityLeft: chargingKpis.weightedUptime.capacityLeft,
          capacityRight: chargingKpis.weightedUptime.capacityRight,
          progressPercent: chargingKpis.weightedUptime.progressPercent,
        }
      : null,
    customerRating,
    dropOffCauses: chargingKpis?.dropOff ?? null,
    ownedSegment,
    managedSegment,
    locationsTitle: t('serviceQuality.locations.title'),
    locationsUrgentNote: t('serviceQuality.locations.urgentNote', {
      count: stats?.urgentSiteCount ?? 0,
    }),
    locationsColumns: columns,
    locationsRows,
    escalationNote,
    onPeriodChange,
    onGenerateReport,
  };
}
